import { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ArrowLeft, Play } from 'lucide-react';

export const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  },
};

export const staggerContainer = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.05 },
  },
};

export const viewportOnce = { once: true, amount: 0.25 };

export function GridGlow({ accent = '#4c8dcc', className = '' }) {
  return (
    <div aria-hidden className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
      <div
        className="absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            'linear-gradient(rgb(var(--site-ink-rgb) / .8) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--site-ink-rgb) / .8) 1px, transparent 1px)',
          backgroundSize: '58px 58px',
          maskImage: 'radial-gradient(ellipse at center, black, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black, transparent 78%)',
        }}
      />
      <div
        className="absolute -top-40 right-[-10%] h-[460px] w-[680px] rounded-full blur-[150px] opacity-[0.18]"
        style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)` }}
      />
    </div>
  );
}

export function Watermark({ children, className = '' }) {
  return (
    <span
      aria-hidden
      className={`pointer-events-none absolute select-none font-serif leading-none text-ink/[0.03] tracking-tighter ${className}`}
    >
      {children}
    </span>
  );
}

export function MagneticButton({
  children,
  className = '',
  onClick,
  strength = 0.35,
  'data-testid': testId,
  type = 'button',
}) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 15, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 200, damping: 15, mass: 0.4 });
  const rotateX = useTransform(sy, [-40, 40], [6, -6]);
  const rotateY = useTransform(sx, [-40, 40], [-6, 6]);

  const handleMove = (event) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const relX = event.clientX - (rect.left + rect.width / 2);
    const relY = event.clientY - (rect.top + rect.height / 2);
    x.set(relX * strength);
    y.set(relY * strength);
  };

  return (
    <motion.button
      ref={ref}
      type={type}
      data-testid={testId}
      onClick={onClick}
      onMouseMove={handleMove}
      onMouseLeave={() => {
        x.set(0);
        y.set(0);
      }}
      style={{ x: sx, y: sy, rotateX, rotateY, transformStyle: 'preserve-3d' }}
      whileTap={{ scale: 0.96 }}
      className={className}
    >
      {children}
    </motion.button>
  );
}

export function CourseThumb({ thumb, alt = '', accent = '#4c8dcc', label = 'Preview', onPlay, 'data-testid': testId }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 150, damping: 20 });
  const sy = useSpring(y, { stiffness: 150, damping: 20 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [8, -8]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-8, 8]);

  const onMove = (event) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((event.clientX - rect.left) / rect.width - 0.5);
    y.set((event.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <motion.button data-theme-surface="dark"
      ref={ref}
      data-testid={testId}
      type="button"
      onClick={onPlay}
      onMouseMove={onMove}
      onMouseLeave={() => {
        x.set(0);
        y.set(0);
      }}
      style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
      className="group relative aspect-[4/3] w-full overflow-hidden rounded-xl border border-ink/10 bg-black text-left"
    >
      {thumb ? (
        <img
          src={thumb}
          alt={alt}
          className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-[1200ms] ease-out group-hover:scale-110"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#4c8dcc]/20 via-white/[0.04] to-black" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
      <div className="absolute inset-0 grid place-items-center">
        <span
          className="grid h-16 w-16 place-items-center rounded-full text-black shadow-[0_0_40px_rgba(76,141,204,0.35)] transition-transform duration-300 group-hover:scale-110"
          style={{ backgroundColor: accent }}
        >
          <Play className="ml-1 h-6 w-6 fill-current" />
        </span>
      </div>
      <div className="absolute bottom-5 left-5 right-5">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-copy/70">{label}</span>
      </div>
    </motion.button>
  );
}

export function CaseNav({ backLabel = 'Back', backTo = '/', actionLabel = 'Reserve', actionTo = '/#reserve' }) {
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 inset-x-0 z-50 glass">
      <div className="container mx-auto px-6 md:px-12 lg:px-20 py-4 flex items-center justify-between">
        <Link
          to={backTo}
          data-testid="case-back"
          className="flex items-center gap-2 text-tone-300 hover:text-ink transition-colors font-mono text-[11px] uppercase tracking-[0.2em]"
        >
          <ArrowLeft size={16} /> {backLabel}
        </Link>
        <Link to="/" className="hidden sm:flex items-center gap-2">
          <span className="font-serif text-xl text-ink">MAGELLAN</span>
          <span className="h-1.5 w-1.5 rounded-full bg-[#4c8dcc]" />
        </Link>
        <button
          type="button"
          data-testid="case-book"
          onClick={() => navigate(actionTo)}
          className="bg-[#4c8dcc] text-black px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#e5c158] transition-colors"
        >
          {actionLabel}
        </button>
      </div>
    </header>
  );
}
